import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { clearCustomerWidgetAuthToken } from "@redotech/redo-customer-portal-app/api";
import { SetAuthContext } from "@redotech/redo-customer-portal-app/contexts/auth";
import { CustomerContext } from "@redotech/redo-customer-portal-app/contexts/CustomerContext";
import { SettingsContext } from "@redotech/redo-customer-portal-app/contexts/settings";
import {
  SetUserContext,
  UserContext,
} from "@redotech/redo-customer-portal-app/contexts/user";
import { useTranslation } from "@redotech/redo-i18n";
import { LocaleSelector } from "@redotech/redo-i18n/locale-selector";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down_filled.svg";
import { getPrimaryCustomerEmail } from "@redotech/redo-model/customer";
import { Avatar } from "@redotech/redo-web/arbiter-components/avatars/avatar";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { getGravatarUrlFromEmail } from "@redotech/redo-web/gravatar-utils";
import { Text } from "@redotech/redo-web/text";
import { allowCustomerAccounts } from "@redotech/redo-web/utils/customer-account-utils";
import { assertNever } from "@redotech/util/type";
import { useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { OrderContext } from "./order";
import * as navbarCss from "./version-four-navbar.module.css";

enum MenuOption {
  MyOrders = "My orders",
  MyAccount = "My account",
  Logout = "Logout",
  MyWarranties = "My warranties",
}

export function VersionFourNavbar() {
  const { t } = useTranslation();
  const settings = useContext(SettingsContext);
  const customerContext = useContext(CustomerContext);
  const order = useContext(OrderContext);
  const navigate = useNavigate();
  const user = useContext(UserContext);
  const setUser = useContext(SetUserContext);
  const setAuth_ = useContext(SetAuthContext);
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLElement | null>(null);

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const screen = useScreenType();

  const primaryEmail = getPrimaryCustomerEmail(customerContext) || user?.email;
  const customerName = customerContext?.name || user?.name;

  const orderName = order?.shopify?.name;

  const menuOptions: RedoListItem<MenuOption>[] = useMemo(() => {
    const optionToLabel: Record<MenuOption, string> = {
      [MenuOption.MyOrders]: t("My Orders"),
      [MenuOption.MyAccount]: t("My Account"),
      [MenuOption.Logout]: t("Logout"),
      [MenuOption.MyWarranties]: t("My Warranties"),
    };

    const myOrdersOption =
      user && !user?.guestEmail && user.email ? [MenuOption.MyOrders] : [];
    const myWarrantiesOption =
      user && !user?.guestEmail && user.email && settings?.warranties?.enabled
        ? [MenuOption.MyWarranties]
        : [];
    const myAccountOption =
      allowCustomerAccounts(settings?.customerAccounts) &&
      !!customerContext &&
      primaryEmail
        ? [MenuOption.MyAccount]
        : [];

    const menuOptions = [
      myOrdersOption,
      myWarrantiesOption,
      myAccountOption,
      MenuOption.Logout,
    ].flat();

    return menuOptions.map((option) => ({
      id: option,
      value: option,
      type: "text",
      text: optionToLabel[option],
    }));
  }, [user, settings, customerContext, primaryEmail, t]);

  if (!user || user?.isMerchant) {
    return null;
  }
  return (
    <Flex
      align="center"
      bgColor={screen === ScreenType.DESKTOP ? "primary" : "secondary"}
      borderBottomWidth="1px"
      borderColor="secondary"
      borderStyle="solid"
      className={navbarCss.navBar}
      data-testid="navbar"
      dir="row"
      justify="space-between"
      position="sticky"
      px={screen === ScreenType.DESKTOP ? "2xl" : "lg"}
      py="lg"
      top="0"
    >
      <Flex align="center" basis="33.33%" dir="row" gap="lg">
        <LocaleSelector />
        {screen === ScreenType.DESKTOP && orderName && (
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            {t("Order {{orderName}}", { orderName })}
          </Text>
        )}
      </Flex>

      <Flex basis="33.33%" justify="center">
        <a
          // @ts-expect-error: strict-null-checks
          href={`https://${settings.brandKit?.customerFacingDomain || settings.storeUrl}`}
          rel="noreferrer"
          target="_blank"
        >
          <img
            alt={t("Store logo")}
            className={navbarCss.navBarLogo}
            id="navbar-logo"
            // @ts-expect-error: strict-null-checks
            src={settings.theme?.logo_image}
          />
        </a>
      </Flex>

      <Flex align="center" basis="33.33%" dir="row" gap="sm" justify="flex-end">
        <RedoListDropdown
          dropdownAnchor={dropdownButtonRef}
          dropdownOpen={dropdownOpen}
          items={menuOptions}
          itemSelected={({ item }) => {
            switch (item.value) {
              case MenuOption.MyOrders:
                navigate("/returns-portal/orders");
                break;
              case MenuOption.MyAccount:
                navigate("/returns-portal/account");
                break;
              case MenuOption.Logout:
                // @ts-expect-error: strict-null-checks
                setAuth_(undefined);
                // @ts-expect-error: strict-null-checks
                setUser(undefined);
                clearCustomerWidgetAuthToken();
                navigate("/returns-portal/login");
                break;
              case MenuOption.MyWarranties:
                navigate("/returns-portal/account/warranties");
                break;
              default:
                assertNever(item.value);
            }
          }}
          setDropdownOpen={setDropdownOpen}
          size="xs"
        />

        <Flex
          align="center"
          className={navbarCss.userMenuTrigger}
          gap="sm"
          onClick={() => setDropdownOpen(!dropdownOpen)}
          ref={setDropdownButtonRef}
        >
          <Flex align="center" dir="row" gap="sm">
            <Avatar
              alt={customerName || primaryEmail || ""}
              imageUrl={
                customerName
                  ? null
                  : primaryEmail
                    ? getGravatarUrlFromEmail(primaryEmail)
                    : null
              }
              name={customerName}
            />
            {screen === ScreenType.DESKTOP && customerName && (
              <Text fontSize="sm" fontWeight="bold">
                {customerName}
              </Text>
            )}
          </Flex>
          <ChevronDownIcon className={navbarCss.chevronIcon} />
        </Flex>
      </Flex>
    </Flex>
  );
}
